import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
/* eslint-disable-next-line import/no-extraneous-dependencies, import/no-unresolved */
import SignatureCanvas from 'react-signature-canvas';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

// ----------------------------------------------------------------------

export default function FormSignature({ name, label, helperText, required, ...other }) {
    const { control, setValue, watch } = useFormContext();
    const sigCanvas = useRef(null);
    const fieldValue = watch(name);

    // Handle pre-loaded data (e.g. from drafts or edits)
    useEffect(() => {
        if (fieldValue && sigCanvas.current && sigCanvas.current.isEmpty && sigCanvas.current.isEmpty()) {
            // Attempt to load the base64 string back into the canvas
            try {
                sigCanvas.current.fromDataURL(fieldValue);
            } catch (e) {
                console.error("Failed to load signature data", e);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValue]);

    const clearSignature = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
            setValue(name, null, { shouldValidate: true });
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? 'This signature is required' : false }}
            render={({ field, fieldState: { error } }) => {

                // Allow controlled saving back to field
                const handleEnd = () => {
                    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
                        // Save signature as a base64 encoded PNG
                        field.onChange(sigCanvas.current.getCanvas().toDataURL('image/png'));
                    }
                };

                return (
                    <Box sx={{ width: '100%' }}>
                        {label && (
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {label} {required && <span style={{ color: 'red' }}>*</span>}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                border: (theme) => `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                                position: 'relative',
                                '& canvas': {
                                    display: 'block',
                                    width: '100%',
                                    minHeight: 150,
                                    cursor: 'crosshair',
                                },
                            }}
                        >
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                backgroundColor="rgba(255,255,255,1)" // Ensure white bg for base64 clarity
                                onEnd={handleEnd}
                                canvasProps={{
                                    className: 'signature-canvas',
                                    style: { width: '100%', height: '100%', minHeight: '150px' }
                                }}
                                {...other}
                            />
                        </Box>

                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                            <Button size="small" color="error" onClick={clearSignature} variant="text">
                                Clear
                            </Button>
                        </Stack>

                        {/* Error / Helper */}
                        {(!!error || helperText) && (
                            <FormHelperText error={!!error} sx={{ mx: 0, mt: -2 }}>
                                {error ? error?.message : helperText}
                            </FormHelperText>
                        )}
                    </Box>
                );
            }}
        />
    );
}

FormSignature.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    helperText: PropTypes.string,
    required: PropTypes.bool,
};
